import React, { useState } from "react";
import { useParams } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Check, ExternalLink, BookOpenText } from "lucide-react";
import { Link } from "react-router-dom";
import GuideDialog from "@/components/GuideDialog";
import SEO from "@/components/SEO";
import { useTranslation } from "react-i18next";

// Define the package data structure to ensure type safety
interface PackageStep {
  title: string;
  description: string;
  benefits: string[];
  url: string;
  ctaText: string;
}

interface PackageData {
  title: string;
  subtitle: string;
  description: string;
  steps: PackageStep[];
}

const PackageDetail = () => {
  const { packageId } = useParams<{ packageId: string }>();
  const { t, i18n } = useTranslation();

  // State to control which guide dialog is open
  const [openGuideId, setOpenGuideId] = useState<string | null>(null);

  // Get translated package data
  const getPackageData = (): Record<string, PackageData> => {
    return {
      snabbstart: {
        title: t("packageDetail.snabbstart.title"),
        subtitle: t("packageDetail.snabbstart.subtitle"),
        description: t("packageDetail.snabbstart.description"),
        steps: t("packageDetail.snabbstart.steps", { returnObjects: true }) as PackageStep[],
      },
      vardagspaketet: {
        title: t("packageDetail.vardagspaketet.title"),
        subtitle: t("packageDetail.vardagspaketet.subtitle"),
        description: t("packageDetail.vardagspaketet.description"),
        steps: t("packageDetail.vardagspaketet.steps", { returnObjects: true }) as PackageStep[],
      },
      "full-frikoppling": {
        title: t("packageDetail.fullFrikoppling.title"),
        subtitle: t("packageDetail.fullFrikoppling.subtitle"),
        description: t("packageDetail.fullFrikoppling.description"),
        steps: t("packageDetail.fullFrikoppling.steps", { returnObjects: true }) as PackageStep[],
      },
      "sociala-media": {
        title: t("packageDetail.socialMedia.title"),
        subtitle: t("packageDetail.socialMedia.subtitle"),
        description: t("packageDetail.socialMedia.description"),
        steps: t("packageDetail.socialMedia.steps", { returnObjects: true }) as PackageStep[],
      },
    };
  };

  const packagesData = getPackageData();
  const packageData = packageId ? packagesData[packageId as keyof typeof packagesData] : null;

  // Map package steps to their corresponding guide IDs
  const getGuideId = (step: PackageStep, index: number): string => {
    // Use predefined set of guideIds based on package and index
    const guideIdMappings: Record<string, string[]> = {
      snabbstart: ["firefox", "signal", "mojeek", "bitwarden"],
      vardagspaketet: ["protonmail", "nextcloud", "bitwarden", "libreoffice", "newpipe"],
      "full-frikoppling": ["graphene", "nextcloud", "mastodon", "adblock"],
      "sociala-media": ["facebook", "instagram", "linkedin", "youtube", "whatsapp", "rss"],
    };

    // Get the guide IDs for this package
    const guideIds = packageId ? guideIdMappings[packageId] : [];

    // Return the corresponding guide ID if available, otherwise generate a fallback ID
    return index < guideIds.length ? guideIds[index] : `${packageId}-step-${index}`;
  };

  if (!packageData) {
    return (
      <div className="min-h-screen flex flex-col">
        <SEO
          title={t("packageDetail.notFound.title")}
          description={t("packageDetail.notFound.description")}
          url="/paket/not-found"
        />
        <Navbar />
        <main className="flex-grow container-custom py-20">
          <h1 className="text-3xl font-bold text-center">{t("packageDetail.notFound.message")}</h1>
          <div className="flex justify-center mt-8">
            <Button asChild>
              <Link to="/paket">
                <ArrowLeft className="mr-2 h-4 w-4" />
                {t("packageDetail.notFound.backButton")}
              </Link>
            </Button>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  // Modified to clearly log the guide ID when opened
  const handleOpenGuide = (guideId: string) => {
    console.log("Opening guide with ID:", guideId, "in language:", i18n.language);
    setOpenGuideId(guideId);
  };

  const handleCloseGuide = () => {
    console.log("Closing guide");
    setOpenGuideId(null);
  };

  return (
    <div className="min-h-screen flex flex-col">
      <SEO
        title={`${packageData?.title} - Frikopplad`}
        description={packageData?.description || ""}
        url={`/paket/${packageId}`}
      />
      <Navbar />
      <main className="flex-grow">
        <div className="bg-frikopplad-cream py-16">
          <div className="container-custom">
            <Link to="/paket" className="inline-flex items-center text-frikopplad-navy hover:text-frikopplad-red mb-4">
              <ArrowLeft className="mr-2 h-4 w-4" />
              {t("packageDetail.backToPackages")}
            </Link>
            <h1 className="text-4xl font-bold text-frikopplad-navy mb-2">{packageData?.title}</h1>
            <p className="text-xl text-gray-600 mb-4">{packageData?.subtitle}</p>
            <p className="max-w-3xl text-gray-700">{packageData?.description}</p>
          </div>
        </div>

        <div className="container-custom py-16">
          <div className="space-y-12">
            {Array.isArray(packageData?.steps) &&
              packageData.steps.map((step, index) => (
                <div key={index} className="border rounded-lg shadow-sm p-6 bg-white">
                  <h3 className="text-2xl font-semibold text-frikopplad-navy mb-2">{step.title}</h3>
                  <p className="text-gray-700 mb-4">{step.description}</p>

                  <h4 className="text-sm font-semibold text-gray-500 mb-2">{t("packageDetail.benefitsLabel")}</h4>
                  <ul className="mb-6 grid grid-cols-1 md:grid-cols-3 gap-2">
                    {step.benefits.map((benefit, i) => (
                      <li key={i} className="flex items-start">
                        <Check className="text-frikopplad-blue h-5 w-5 mr-2 flex-shrink-0 mt-0.5" />
                        <span className="text-sm">{benefit}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="flex flex-wrap gap-3">
                    <Button className="bg-frikopplad-blue hover:bg-frikopplad-navy" asChild>
                      <a href={step.url} target="_blank" rel="noopener noreferrer">
                        {step.ctaText}
                        <ExternalLink className="ml-2 h-4 w-4" />
                      </a>
                    </Button>

                    <Button
                      variant="outline"
                      className="border-frikopplad-blue text-frikopplad-blue hover:bg-frikopplad-blue/10"
                      onClick={() => handleOpenGuide(getGuideId(step, index))}
                    >
                      <BookOpenText className="mr-2 h-4 w-4" />
                      {t("packageDetail.stepByStepGuide")}
                    </Button>
                  </div>
                </div>
              ))}
          </div>
        </div>
      </main>

      {/* Pass the current packageId and openGuideId to GuideDialog */}
      <GuideDialog
        isOpen={!!openGuideId}
        onClose={handleCloseGuide}
        guideId={openGuideId || ""}
        packageId={packageId || ""}
      />

      <Footer />
    </div>
  );
};

export default PackageDetail;
